import { onMounted } from "vue";
import type { IComposableInterface } from "./types";
import { useCommon } from "./use-common";
import { ValidationError } from "./validation.error";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useGuestStore } from "~/stores/guest";
import type { IDomainAddress } from "~/domain/address";
import { useLocale } from "~/composables/use-locale";
import { isUsableCoordinate } from "~/utils/is-usable-coordinate";

export const useGuestAddress = (): IComposableInterface => {
  const business = useBusinessStore();
  const basket = useBasketStore();
  const guest = useGuestStore();

  const { t } = useLocale();

  const {
    formErrors,
    isLoading,
    isSaving,
    record,
    setFieldValue,
    noop,
    handleSaveError,
  } = useCommon();

  const saveAddress = async (onSuccess: () => void = noop) => {
    formErrors.value = {};
    isSaving.value = true;

    try {
      if (
        !record.value?.location ||
        !isUsableCoordinate(record.value) ||
        (business.config.isPostalCodeRequired && !record.value?.zipCode)
      ) {
        const fieldErrors: Partial<Record<keyof IDomainAddress, string>> = {};

        if (!isUsableCoordinate(record.value)) {
          fieldErrors.lat = t(
            "components.address_form.error_message_coordinates",
          );
        }

        if (!record.value.location) {
          fieldErrors.location = t(
            "components.address_form.error_message_location",
          );
        }

        if (business.config.isPostalCodeRequired && !record.value?.zipCode) {
          fieldErrors.zipCode = t(
            "components.address_form.error_message_zip_code",
          );
        }

        throw new ValidationError("Address missing params", fieldErrors);
      }

      await guest.addAddress({
        lat: record.value.lat,
        lng: record.value.lng,
        location: record.value.location,
        number: record.value.number,
        city: record.value.city,
        country: business.country,
        zipCode: record.value.zipCode,
        instruction: record.value.instruction,
      });

      onSuccess();
    } catch (e) {
      handleSaveError(e);
    } finally {
      isSaving.value = false;
    }
  };

  const loadAddress = () => {
    const guestAddress = guest.address;
    const basketCurrentLocation = basket.currentLocation;

    if (!guestAddress && !basketCurrentLocation) {
      return;
    }

    record.value = {
      lat: guestAddress?.lat ?? basketCurrentLocation?.coordinate?.lat ?? 0,
      lng: guestAddress?.lng ?? basketCurrentLocation?.coordinate?.lng ?? 0,
      location: guestAddress?.location ?? basketCurrentLocation?.address ?? "",
      number: guestAddress?.number ?? "",
      city: guestAddress?.city ?? basketCurrentLocation?.city ?? "",
      zipCode: guestAddress?.zipCode ?? basketCurrentLocation?.postalCode ?? "",
      instruction: guestAddress?.instruction ?? "",
    };
  };

  onMounted(() => {
    loadAddress();
    isLoading.value = false;
  });

  return {
    isLoading,
    isSaving,
    record,
    formErrors,
    setFieldValue,
    saveAddress,
  };
};
